import { FetchCallback, get, post, postWithToast } from 'utils/fetch';
import * as Types from './documents.types';
import {
    CertificateDocModel,
    CertificateEducationDocModel,
    DiplomDocModel,
    TicketBModel,
    CertificateThemeModelListResponseWithDataModel,
    DocumentType
} from 'api/api';
import { useNumberId } from 'hooks/useId';
import { OptionProps } from 'ui/Select/Select.types';
import { getMessage } from 'messages';
import { toast } from 'react-toastify';
import { ResponseCodes } from 'helpers/constants';
import { Button } from 'ui/Button';
import { RouterPaths } from 'helpers/router-paths';
import { NavigateFunction, useNavigate } from 'react-router-dom';

export const generateDocument =
    (
        type: DocumentType,
        navigate: NavigateFunction,
        assignId: number,
        props: object
    ): FetchCallback =>
    (onSuccess, onError) =>
        post(
            `Document/${type}`,
            { ...props, assignId },
            undefined,
            false
        )(
            (res) => {
                onSuccess?.(res);
                navigate(RouterPaths.CourseAssignment(assignId), {
                    unstable_viewTransition: true
                });
            },
            (err) => {
                let msg: React.ReactNode = err.msg || getMessage(err.code);

                const isPayError = err.code === ResponseCodes.AssignNotPaid;

                if (isPayError) {
                    msg = (
                        <>
                            {msg}
                            <div
                                style={{
                                    marginTop: '10px',
                                    width: 'max-content'
                                }}
                            >
                                <Button
                                    size="s"
                                    href={RouterPaths.PayAssign(assignId)}
                                >
                                    Оплатить назначение
                                </Button>
                            </div>
                        </>
                    );
                }
                toast.error(msg, {
                    className: isPayError ? 'align-top' : undefined
                });
                onError?.(err);
            }
        );

export const getCertificateInfo = (id: number) =>
    get<CertificateDocModel>(`Document/GetCertificateInfo/${id}`);

export const getDiplomInfo = (id: number) =>
    get<DiplomDocModel>(`Document/GetDiplomInfo//${id}`);

export function useGenerateDocument<T extends object = object>(
    path: DocumentType
) {
    const assignId = useNumberId();
    const navigate = useNavigate();
    return (props: T) => generateDocument(path, navigate, assignId, props);
}

export const generateCertificateOfStudy = (
    props: CertificateEducationDocModel
) =>
    postWithToast<CertificateEducationDocModel>(
        `Document/CertificateEducation`,
        props,
        {
            download: true
        }
    );

// eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-explicit-any
export const generateInvoice = (props: any) =>
    postWithToast(`Document/PaymentInvoice`, props, {
        download: true
    });

export const getTicketBInfo = (id: number) =>
    get<TicketBModel>(`Document/GetTicketBInfo/${id}`);

export const getTicketMInfo = () =>
    get<Types.TicketM>(`Document/GetTicketMInfo`);

export const generateTicketM = (props: Types.TicketM) =>
    postWithToast('Document/TicketM', props, { download: true });

export const getThemes = (search?: string) =>
    get<CertificateThemeModelListResponseWithDataModel['data']>(
        `CertificateTheme/GetThemes${search ? `?search=${search}` : ''}`
    );

export const getThemesOptions = async () => {
    const themes = await getThemes();
    return (
        themes?.map(
            (theme): OptionProps<string> => ({
                value: theme.title ?? '',
                label: theme.title ?? ''
            })
        ) ?? []
    );
};
